import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api, json } from '../api/client'
import type { EpisodeDetail, EpisodeSummary, ImportSummary, LiveSnapshot, Medium, Recording, Show, Waveform } from '../api/types'

const workspaceKey = (ws: string) => ['workspace', ws] as const
export const showsKey = (ws: string) => [...workspaceKey(ws), 'shows'] as const
const showKey = (ws: string, id: string) => [...showsKey(ws), id] as const
export const episodesKey = (ws: string) => [...workspaceKey(ws), 'episodes'] as const
const showEpisodesKey = (ws: string, showId: string) => [...episodesKey(ws), showId] as const
export const episodeKey = (ws: string, id: string) => [...workspaceKey(ws), 'episode', id] as const
export const billingKey = (ws: string) => [...workspaceKey(ws), 'billing'] as const

const ACTIVE = new Set(['uploading', 'processing'])

/** Refetch an episode, every episode list and the balance (paid work holds credits) after something changed it. */
export function refreshEpisode(queryClient: QueryClient, ws: string, id: string) {
  void queryClient.invalidateQueries({ queryKey: episodeKey(ws, id) })
  void queryClient.invalidateQueries({ queryKey: episodesKey(ws) })
  void queryClient.invalidateQueries({ queryKey: billingKey(ws) })
}

export function useShows(ws: string) {
  return useQuery({ queryKey: showsKey(ws), queryFn: () => api<Show[]>(`/workspaces/${ws}/shows`) })
}

export function useShow(ws: string, id: string) {
  return useQuery({ queryKey: showKey(ws, id), queryFn: () => api<Show>(`/workspaces/${ws}/shows/${id}`) })
}

export interface ShowInput {
  title: string
  description: string
  medium: Medium
  voice_prompt: string
  /** Directory details; only saved on an existing show. */
  author?: string
  owner_email?: string
  category?: string
  language?: string
  explicit?: boolean
}

/** Create a show, or update it when `id` is given. */
export function useSaveShow(ws: string, id?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ShowInput) =>
      id
        ? api<Show>(`/workspaces/${ws}/shows/${id}`, { method: 'PATCH', body: json(input) })
        : api<Show>(`/workspaces/${ws}/shows`, { method: 'POST', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: showsKey(ws) }),
  })
}

export function useDeleteShow(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<void>(`/workspaces/${ws}/shows/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workspaceKey(ws) }),
  })
}

export function useEpisodes(ws: string, showId: string) {
  return useQuery({
    queryKey: showEpisodesKey(ws, showId),
    queryFn: () => api<EpisodeSummary[]>(`/workspaces/${ws}/shows/${showId}/episodes`),
    refetchInterval: (query) => (query.state.data?.some((e) => ACTIVE.has(e.status)) ? 3_000 : false),
  })
}

/** A show's original recordings with links that save them. Signed for the hour, so kept no longer than that. */
export function useRecordings(ws: string, showId: string) {
  return useQuery({
    queryKey: [...showEpisodesKey(ws, showId), 'recordings'] as const,
    queryFn: () => api<Recording[]>(`/workspaces/${ws}/shows/${showId}/recordings`),
    staleTime: 30 * 60_000,
  })
}

export function useCreateEpisode(ws: string, showId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { title: string }) =>
      api<EpisodeSummary>(`/workspaces/${ws}/shows/${showId}/episodes`, { method: 'POST', body: json(input) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: episodesKey(ws) })
      void queryClient.invalidateQueries({ queryKey: showsKey(ws) })
    },
  })
}

export function useEpisode(ws: string, id: string) {
  return useQuery({ queryKey: episodeKey(ws, id), queryFn: () => api<EpisodeDetail>(`/workspaces/${ws}/episodes/${id}`) })
}

export function useUpdateEpisode(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { title?: string; description?: string }) =>
      api<EpisodeDetail>(`/workspaces/${ws}/episodes/${id}`, { method: 'PATCH', body: json(input) }),
    onSuccess: (detail) => {
      queryClient.setQueryData(episodeKey(ws, id), detail)
      void queryClient.invalidateQueries({ queryKey: episodesKey(ws) })
    },
  })
}

export function useDeleteEpisode(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<void>(`/workspaces/${ws}/episodes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: episodesKey(ws) })
      void queryClient.invalidateQueries({ queryKey: showsKey(ws) })
    },
  })
}

export function useReprocessEpisode(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ pipeline_id: string }>(`/workspaces/${ws}/episodes/${id}/reprocess`, { method: 'POST' }),
    onSuccess: () => refreshEpisode(queryClient, ws, id),
  })
}

export function useWaveform(ws: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...episodeKey(ws, id), 'waveform'],
    queryFn: () => api<Waveform>(`/workspaces/${ws}/episodes/${id}/waveform`),
    enabled,
    staleTime: Infinity,
  })
}

function signature(status: string, assetCount: number, medium: string | null, transcript: string, text = ''): string {
  return `${status}|${assetCount}|${medium ?? ''}|${transcript}|${text}`
}

/**
 * Follow an episode's processing live over server-sent events. Returns the
 * latest snapshot, and refetches the full episode (with its transcript and text)
 * when its status, files, medium, transcript, or text, clip and thumbnail jobs change.
 */
export function useEpisodeLive(ws: string, id: string): LiveSnapshot | null {
  const queryClient = useQueryClient()
  const [live, setLive] = useState<LiveSnapshot | null>(null)
  useEffect(() => {
    setLive(null)
    const source = new EventSource(`/api/v1/workspaces/${ws}/episodes/${id}/events`)
    let previous: string | null = null
    const onEpisode = (event: Event) => {
      const snapshot = JSON.parse((event as MessageEvent<string>).data) as LiveSnapshot
      setLive(snapshot)
      const cached = queryClient.getQueryData<EpisodeDetail>(episodeKey(ws, id))
      const baseline = previous ?? (cached ? signature(cached.status, cached.assets.length, cached.medium, cached.transcript_status) : null)
      const next = signature(
        snapshot.status,
        snapshot.asset_count,
        snapshot.medium,
        snapshot.transcript_status,
        snapshot.side_jobs.map((t) => `${t.job_type}:${t.clip_id ?? ''}:${t.status}`).join(','),
      )
      if (baseline !== null && baseline !== next) refreshEpisode(queryClient, ws, id)
      previous = next
    }
    source.addEventListener('episode', onEpisode)
    return () => {
      source.removeEventListener('episode', onEpisode)
      source.close()
    }
  }, [ws, id, queryClient])
  return live
}

/** Upload a show's artwork: the image's bytes as the request body. */
export function useUploadArtwork(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) =>
      api<Show>(`/workspaces/${ws}/shows/${id}/artwork`, { method: 'PUT', body: file, headers: { 'content-type': file.type || 'application/octet-stream' } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: showsKey(ws) }),
  })
}

/** Publish now, or on a date (a future one schedules it). Null takes the default. */
export function usePublishEpisode(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { published_at: string | null }) =>
      api<EpisodeDetail>(`/workspaces/${ws}/episodes/${id}/publish`, { method: 'POST', body: json(input) }),
    onSuccess: (detail) => {
      queryClient.setQueryData(episodeKey(ws, id), detail)
      refreshEpisode(queryClient, ws, id)
      void queryClient.invalidateQueries({ queryKey: showsKey(ws) })
    },
  })
}

export function useUnpublishEpisode(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<EpisodeDetail>(`/workspaces/${ws}/episodes/${id}/unpublish`, { method: 'POST' }),
    onSuccess: (detail) => {
      queryClient.setQueryData(episodeKey(ws, id), detail)
      refreshEpisode(queryClient, ws, id)
      void queryClient.invalidateQueries({ queryKey: showsKey(ws) })
    },
  })
}

/** Give an episode its recording from a link; a worker copies it in, then it processes like an upload. */
export function useImportSourceUrl(ws: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ episodeId, url }: { episodeId: string; url: string }) =>
      api<{ asset_id: string; job_id: string }>(`/workspaces/${ws}/episodes/${episodeId}/source/url`, { method: 'POST', body: json({ url }) }),
    onSuccess: (_result, { episodeId }) => refreshEpisode(queryClient, ws, episodeId),
  })
}

/** Bring a show over from another host's RSS feed. */
export function useImportFeed(ws: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { feed_url: string; limit: number }) => api<ImportSummary>(`/workspaces/${ws}/imports`, { method: 'POST', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workspaceKey(ws) }),
  })
}
